/*
 *  Copyright (c) 2003-2019 GameDuell GmbH, All Rights Reserved
 *  This document is strictly confidential and sole property of GameDuell GmbH, Berlin, Germany
 */
import { State } from './state'
import { ZoneSelection, ZoneSelectionUi } from '@/model/ZoneSelection'
import { Dates, TournamentUi, GameUi } from '@/model/Tournament'
import { TournamentStatus } from '@/model/TournamentStatus'
import { getTournamentType, isMasters, createGamesUi, formatMetaData, tournamentDefaultImages } from './lib/util'
import { gdcConverter } from '@gd/currency-converter'
import GameState from '@/model/GameState'

/**
 * Notes:
 * zoneId's doesn't contain the postfix: _gd
 *   -> For detecting if it is a internal zone please use the tournamentsType (TOURNAMENT_TYPE) property
 *
 * TODO: outsource the private helper function - but ATTENTION: actually the helper function should only be
 * used inside THIS getters.ts - If you need them somewhere else think about twice - you probaly do something wrong!!!
 */

/**
 * make sure that all properties in the getters object are also in the Getters model
 */
export interface Getters {
  isMastersSelected: boolean
  zoneTeasers: Array<ZoneSelectionUi>
  tournamentUi: TournamentUi
}

function isRegistrationPossible(dates: Dates) {
  const actualD = new Date()
  const start = new Date(dates.registrationStart)
  const end = new Date(dates.registrationEnd)
  return actualD.getTime() > start.getTime() && actualD.getTime() < end.getTime()
}

function getTournamentStatus(dates: Dates): TournamentStatus {
  const now = new Date().getTime()
  const compStart = new Date(dates.competitionStart).getTime()
  const compEnd = new Date(dates.competitionEnd).getTime()

  if (!compStart || !compEnd) {
    return TournamentStatus.NONE
  } else if (now < compStart) {
    return TournamentStatus.UPCOMING
  } else if (now >= compStart && now < compEnd) {
    return TournamentStatus.RUNNING
  } else if (now >= compEnd) {
    return TournamentStatus.FINISHED
  }
}

/**
 * Go though all games and check if there are finshed
 * @param state
 */
function allGamesPlayed(games: Array<GameUi>, numberOfGames: number): boolean {
  return (
    games.filter((game) => {
      return game.state === GameState.FINISHED
    }).length >= numberOfGames
  )
}

function hasGamesToPlay(games: Array<GameUi>): boolean {
  return (
    games.filter((game) => {
      return game.state === GameState.OPEN
    }).length > 0
  )
}

const getters = {
  userId: (state: State): string => `u${state.user.id}`,
  // use this only inside a selected zone -> maybe a zone module would be better to scope it
  isMastersSelected: (state: State): boolean => isMasters(state.zone.zoneId),
  zoneTeasers: (state: State, getters: any) => {
    return state.zoneTeasers.map((zoneTeaser: ZoneSelection): ZoneSelectionUi => {
      return {
        ...zoneTeaser,
        // there are some checks if the t-info are exists so makes sure to return with null if no tournament is available
        tournamentInfo: !zoneTeaser.tournamentInfo
          ? null
          : {
              ...zoneTeaser.tournamentInfo,
              totalWin: zoneTeaser.tournamentInfo
                ? gdcConverter(zoneTeaser.tournamentInfo.totalWin, {
                    gdc: state.user.currency,
                  })
                : '',
              // make sure zone teasers have a default image
              image: zoneTeaser.tournamentInfo.image || tournamentDefaultImages[zoneTeaser.gametype],
            },
        tournamentType: getTournamentType(zoneTeaser.gametype, zoneTeaser.zoneId),
        isMasters: isMasters(zoneTeaser.zoneId),
        tournamentStatus: zoneTeaser.tournamentInfo
          ? getTournamentStatus(zoneTeaser.tournamentInfo.dates)
          : TournamentStatus.NONE,
        // we don't differentiate between normal and internal
        zoneId: zoneTeaser.zoneId.replace('_gd', ''),
        lobbyTimers: !zoneTeaser.tournamentInfo
          ? null
          : getters['lobbyTimers/all'](zoneTeaser.tournamentInfo.tournamentId),
      }
    })
  },
  tournamentUi: (state: State, getters: any): TournamentUi => {
    const games = createGamesUi(state.tournament.games, state.tournament.metaData.numberOfGames)
    return {
      ...state.tournament,
      metaData: formatMetaData(state.tournament.metaData, state.user.currency, state.tournament.gametype),
      tournamentType: getTournamentType(state.tournament.gametype, state.zone.zoneId),
      isMasters: isMasters(state.zone.zoneId),
      isRegistrationPossible: isRegistrationPossible(state.tournament.dates),
      tournamentStatus: getTournamentStatus(state.tournament.dates),
      // we don't differentiate between normal and internal
      zoneId: state.zone.zoneId.replace('_gd', ''),
      games,
      // all games are played ( is not the same as tournamentType.FINISHED )
      allGamesPlayed: allGamesPlayed(games, state.tournament.metaData.numberOfGames),
      // it contains games to play (keep in mind that exists combination of 11 from 12 games are played and the 12th is pending)
      hasNextGame: hasGamesToPlay(games),
      lobbyTimers: state.tournament.tournamentId ? getters['lobbyTimers/all'](state.tournament.tournamentId) : null,
    }
  },
}

export { getters }
